import { useState } from 'react';
import { Alert, Badge, Button, Card, Group, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@prooffleet/client';

interface OpenDefect {
  _id: string;
  label: string;
  severity: string;
  status: string;
  createdAt: string;
  measurement: { kind: string; value: number; threshold: number | null } | null;
}

/**
 * Инструктаж перед осмотром.
 *
 * Следующий водитель обязан увидеть незакрытые дефекты ДО начала своего
 * осмотра и подтвердить. Это и безопасность, и сильнейшее доказательство:
 * видно, что информация дошла до человека, а не осталась в системе.
 */
export function Briefing(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { unitId = '' } = useParams();
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['open-defects', unitId],
    queryFn: () => api<{ defects: OpenDefect[] }>(`/units/${unitId}/open-defects`),
  });

  const defects = data?.defects ?? [];

  const proceed = async (): Promise<void> => {
    setBusy(true);
    try {
      if (defects.length > 0) {
        await api('/defects/acknowledge', {
          method: 'POST',
          body: { unitId, defectIds: defects.map((d) => d._id) },
        });
      }
      void navigate(`/units/${unitId}/inspect`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pf-screen">
      <div className="pf-scroll">
        <Stack gap="md" maw={640} mx="auto" w="100%">
          <Title order={1}>{t('briefing.title')}</Title>

          {isLoading && <Text>{t('common.loading')}</Text>}

          {!isLoading && defects.length === 0 && (
            <Alert color="blue" variant="light">
              {t('briefing.none')}
            </Alert>
          )}

          {defects.length > 0 && <Text c="dimmed">{t('briefing.subtitle')}</Text>}

          {defects.map((defect) => (
            <Card key={defect._id}>
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <div>
                  <Text fw={600}>{defect.label}</Text>
                  <Text c="dimmed" fz="sm">
                    {t('briefing.reported', {
                      date: new Date(defect.createdAt).toLocaleDateString(),
                    })}
                  </Text>
                  {defect.measurement !== null && (
                    <Text fz="sm" mt={4}>
                      {defect.measurement.value}
                      {defect.measurement.threshold !== null && ` / ${defect.measurement.threshold}`}
                    </Text>
                  )}
                </div>
                <Badge
                  size="lg"
                  color={defect.severity === 'critical' ? 'red' : 'orange'}
                  variant="filled"
                >
                  {defect.severity}
                </Badge>
              </Group>
            </Card>
          ))}
        </Stack>
      </div>

      <div className="pf-action-bar">
        <Button fullWidth loading={busy} onClick={() => void proceed()}>
          {defects.length > 0 ? t('briefing.acknowledge') : t('inspection.title')}
        </Button>
      </div>
    </div>
  );
}
